import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Tilt } from 'react-tilt';
import TeamSection from '../components/TeamSection';
import { useTheme } from '../context/ThemeContext';

/* ----------------------------- Helpers ----------------------------- */
function CountUp({ to = 10, duration = 1200, suffix = '+' }) { // ✅ 10+
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf, start;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min(1, (t - start) / duration);
      setN(Math.round(to * p));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to, duration]);
  return <>{n}{suffix}</>;
}

/** initials avatar (for founders list) */
function initialsAvatar(name = 'User') {
  const parts = name.trim().split(' ');
  const initials = ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase();
  const bg1 = '#1FA550';
  const bg2 = '#06B6D4';
  const svg = encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='96' height='96'>
      <defs>
        <linearGradient id='g' x1='0' x2='1' y1='0' y2='1'>
          <stop offset='0%' stop-color='${bg1}'/>
          <stop offset='100%' stop-color='${bg2}'/>
        </linearGradient>
      </defs>
      <rect width='100%' height='100%' rx='48' fill='url(#g)'/>
      <text x='50%' y='54%' dominant-baseline='middle' text-anchor='middle'
            font-family='Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial'
            font-size='36' fill='white' font-weight='700'>${initials}</text>
    </svg>`
  );
  return `data:image/svg+xml;charset=UTF-8,${svg}`;
}

function AvatarImage({ name, srcs = [], className = '' }) {
  const [idx, setIdx] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const sources = [...srcs, initialsAvatar(name)];
  const src = sources[Math.min(idx, sources.length - 1)];
  return (
    <div className={`relative overflow-hidden rounded-full ${className}`}>
      {!loaded && (
        <div className="absolute inset-0 animate-pulse rounded-full"
             style={{ background: 'linear-gradient(90deg, rgba(2,6,23,.06), rgba(2,6,23,.12), rgba(2,6,23,.06))' }} />
      )}
      <img
        src={src}
        alt={name}
        className={`h-full w-full object-cover rounded-full transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setLoaded(true)}
        onError={() => setIdx((i) => i + 1)}
      />
    </div>
  );
}

/* ------------------------------ Page ------------------------------- */
export default function About() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < 768);
    handle();
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);

  const TOKENS = useMemo(() => (
    isDark
      ? {
          bgStart: '#0B1220',
          bgMid:   '#0B1325',
          bgEnd:   '#000000',
          grid:    'rgba(255,255,255,0.24)',
          text:    '#FFFFFF',
          muted:   'rgba(255,255,255,0.78)',
          surface: 'rgba(14,20,34,0.88)',
          line:    'rgba(255,255,255,0.10)',
          shadow:  '0 28px 64px rgba(0,0,0,0.45)',
          orb1:    'rgba(31,165,80,0.22)',
          orb2:    'rgba(6,182,212,0.18)',
          wash:    'rgba(255,255,255,0.04)',
        }
      : {
          bgStart: '#F6FAFF',
          bgMid:   '#EEF3F9',
          bgEnd:   '#FFFFFF',
          grid:    'rgba(0,0,0,0.08)',
          text:    '#0B1220',
          muted:   'rgba(17,24,39,0.66)',
          surface: '#FFFFFF',
          line:    'rgba(2,6,23,0.08)',
          shadow:  '0 32px 72px rgba(2,6,23,0.12)',
          orb1:    'rgba(31,165,80,0.14)',
          orb2:    'rgba(6,182,212,0.12)',
          wash:    'rgba(2,6,23,0.03)',
        }
  ), [isDark]);

  const heroVariants = { hidden: { opacity: 0, y: 18 }, visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } } };

  /* ---------- Timeline (2026/27 = Roadmap) ---------- */
  const timeline = [
    { year: '2025', title: 'Founded', desc: 'MonkeyTech was founded in 2025 to ship clean, outcome-driven software.', icon: '🚀' },
    { year: '2026', title: 'Roadmap • First International Client', desc: 'Cross-border delivery & 24×7 support expansion.', icon: '🌍', roadmap: true },
    { year: '2027', title: 'Roadmap • Innovation Lab', desc: 'AI agents, chatbots & personalization R&D.', icon: '💡', roadmap: true },
  ];

  return (
    <main className="min-h-screen font-inter relative overflow-hidden"
          role="main" aria-label="MonkeyTech About Page" style={{ color: TOKENS.text }}>

      {/* ---------- Background ---------- */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${TOKENS.bgStart}, ${TOKENS.bgMid}, ${TOKENS.bgEnd})` }} />
        <div className="absolute -top-24 -left-24 h-[36rem] w-[36rem] rounded-full blur-3xl"
             style={{ background: `radial-gradient(closest-side, ${TOKENS.orb1}, transparent)` }} />
        <div className="absolute bottom-[-8rem] right-[-8rem] h-[30rem] w-[30rem] rounded-full blur-3xl"
             style={{ background: `radial-gradient(closest-side, ${TOKENS.orb2}, transparent)` }} />
        <div className="absolute inset-0 opacity-[0.06] [mask-image:linear-gradient(to_bottom,black,transparent_96%)]"
             style={{
               backgroundImage: `linear-gradient(${TOKENS.grid} 1px, transparent 1px), linear-gradient(90deg, ${TOKENS.grid} 1px, transparent 1px)`,
               backgroundSize: '44px 44px',
             }} />
      </div>

      {/* ---------- JSON-LD ---------- */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'MonkeyTech',
            foundingDate: '2025',
            founder: [
              { '@type': 'Person', name: 'Kaushal Tiwari', jobTitle: 'Founder & CEO', sameAs: 'https://www.linkedin.com/in/kaushal-tiwari-294628227/' },
              { '@type': 'Person', name: 'Vishal Rathi', jobTitle: 'Co-Founder & CTO', sameAs: 'https://www.linkedin.com/in/vishal-rathi-b24904275/' },
            ],
            description: 'MonkeyTech builds modern websites, apps, AI agents, chatbots and BI dashboards.',
            url: 'https://www.monkeytech.com',
          }),
        }}
      />

      {/* =================== HERO =================== */}
      <motion.section className="relative py-24 sm:py-28 lg:py-32"
                      variants={heroVariants} initial="hidden" animate="visible" aria-labelledby="about-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 items-center gap-10">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
                  style={{ border: `1px solid ${TOKENS.line}`, background: 'rgba(31,165,80,0.10)' }}>
              Founded 2025 • India • 10+ Projects {/* ✅ */}
            </span>

            <h1 id="about-heading" className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
              About{' '}
              <span className="bg-clip-text text-transparent"
                    style={{ backgroundImage: 'linear-gradient(90deg, var(--brand, #1FA550), var(--accent, #06B6D4))' }}>
                MonkeyTech
              </span>
            </h1>

            <p className="mt-4 text-lg sm:text-xl max-w-2xl" style={{ color: TOKENS.muted }}>
              We’re a 2025-born tech studio delivering websites, apps, AI agents, chatbots & BI — with clean design and measurable impact.
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <a href="/contact"
                 className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm sm:text-base font-semibold text-black"
                 style={{ background: 'linear-gradient(135deg, var(--brand, #1FA550), var(--accent, #06B6D4))', boxShadow: TOKENS.shadow }}>
                Get a Free Roadmap
              </a>
              <a href="#timeline"
                 className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm sm:text-base font-semibold"
                 style={{ border: `1px solid ${TOKENS.line}`, background: 'transparent' }}>
                Our Journey
              </a>
            </div>

            {/* KPIs */}
            <ul className="mt-8 grid grid-cols-3 gap-4 max-w-lg">
              <li className="rounded-2xl ring-1 p-4 text-center backdrop-blur"
                  style={{ borderColor: TOKENS.line, background: TOKENS.wash, boxShadow: TOKENS.shadow }}>
                <div className="text-2xl font-extrabold"><CountUp to={10} suffix="+" /></div> {/* ✅ */}
                <div className="text-sm" style={{ color: TOKENS.muted }}>Projects</div>
              </li>
              <li className="rounded-2xl ring-1 p-4 text-center backdrop-blur"
                  style={{ borderColor: TOKENS.line, background: TOKENS.wash, boxShadow: TOKENS.shadow }}>
                <div className="text-2xl font-extrabold">4.9/5</div>
                <div className="text-sm" style={{ color: TOKENS.muted }}>Client CSAT</div>
              </li>
              <li className="rounded-2xl ring-1 p-4 text-center backdrop-blur"
                  style={{ borderColor: TOKENS.line, background: TOKENS.wash, boxShadow: TOKENS.shadow }}>
                <div className="text-2xl font-extrabold">2</div>
                <div className="text-sm" style={{ color: TOKENS.muted }}>Founders</div>
              </li>
            </ul>
          </div>

          {/* Visual (Tilt) */}
          <div className="lg:col-span-5">
            <Tilt options={{ max: 16, scale: 1.02, speed: 450, glare: true, 'max-glare': 0.15 }}>
              <div className="rounded-2xl overflow-hidden ring-1"
                   style={{ borderColor: TOKENS.line, background: TOKENS.surface, boxShadow: TOKENS.shadow }}>
                <div className="relative">
                  <img
                    src="/images/About.png"
                    alt="About MonkeyTech"
                    className="w-full h-auto object-cover"
                    onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/960x600?text=MonkeyTech+About'; }}
                    loading="lazy"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-black/25 via-transparent to-transparent" />
                </div>
              </div>
            </Tilt>
          </div>
        </div>
      </motion.section>

      {/* =================== FOUNDERS (mini) =================== */}
      <section className="py-12 sm:py-14 lg:py-16" aria-labelledby="founders-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 id="founders-heading" className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            Founding Team
          </h2>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              { name: 'Kaushal Tiwari', role: 'Founder & CEO', link: 'https://www.linkedin.com/in/kaushal-tiwari-294628227/', srcs: ['/images/kaushal.jpg', '/images/kaushal.png'] },
              { name: 'Vishal Rathi', role: 'Co-Founder & CTO', link: 'https://www.linkedin.com/in/vishal-rathi-b24904275/', srcs: ['/images/vishal.jpg', '/images/vishal.png', '/images/Vishal.jpg', '/images/Vishal.png'] },
            ].map((p) => (
              <a key={p.name} href={p.link} target="_blank" rel="noopener noreferrer"
                 className="relative rounded-2xl p-[1.5px] transition-transform hover:scale-[1.01]"
                 style={{ background: 'linear-gradient(135deg, var(--brand, #1FA550), var(--accent, #06B6D4))' }}>
                <div className="flex items-center gap-4 rounded-2xl p-5 ring-1"
                     style={{ background: TOKENS.surface, borderColor: TOKENS.line, boxShadow: TOKENS.shadow }}>
                  <AvatarImage name={p.name} srcs={p.srcs} className="h-14 w-14" />
                  <div className="min-w-0">
                    <div className="text-base font-semibold truncate">{p.name}</div>
                    <div className="text-sm truncate" style={{ color: TOKENS.muted }}>{p.role}</div>
                  </div>
                  <span className="ml-auto text-xs px-2 py-1 rounded-full"
                        style={{ border: `1px solid ${TOKENS.line}`, background: TOKENS.wash }}>
                    LinkedIn →
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* =================== MISSION =================== */}
      <section className="py-16 sm:py-20 lg:py-24" aria-labelledby="mission-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h2 id="mission-heading" className="text-4xl sm:text-5xl font-extrabold tracking-tight">
              Our <span className="bg-clip-text text-transparent"
                        style={{ backgroundImage: 'linear-gradient(90deg, var(--brand, #1FA550), var(--accent, #06B6D4))' }}>Mission</span>
            </h2>
            <p className="mt-4 text-lg" style={{ color: TOKENS.muted }}>
              Ship fast, ship clean — and tie every feature to a measurable business outcome.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { t: 'AI-First', d: 'Agents, chatbots & automation that actually save time.' },
              { t: 'Craft', d: 'Thoughtful UX, design systems & micro-interactions.' },
              { t: 'Reliability', d: 'Clear scopes, uptime SLAs, and analytics baked-in.' },
            ].map((x) => (
              <div key={x.t} className="rounded-2xl ring-1 p-6 backdrop-blur"
                   style={{ borderColor: TOKENS.line, background: TOKENS.surface, boxShadow: TOKENS.shadow }}>
                <div className="text-lg font-semibold">{x.t}</div>
                <p className="mt-1 text-sm" style={{ color: TOKENS.muted }}>{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =================== TIMELINE =================== */}
      <section id="timeline" className="py-16 sm:py-20 lg:py-24" aria-labelledby="timeline-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 id="timeline-heading" className="text-4xl sm:text-5xl font-extrabold tracking-tight text-center">
            Our <span className="bg-clip-text text-transparent"
                      style={{ backgroundImage: 'linear-gradient(90deg, var(--brand, #1FA550), var(--accent, #06B6D4))' }}>Journey</span>
          </h2>

          <div className="mt-12 relative">
            <div className="absolute left-4 sm:left-1/2 top-0 bottom-0 w-[2px]"
                 style={{ background: 'linear-gradient(180deg, var(--brand, #1FA550), var(--accent, #06B6D4))', transform: 'translateX(-50%)' }} />
            {timeline.map((ev, i) => {
              const left = isMobile ? false : i % 2 === 0;
              return (
                <motion.article
                  key={ev.year}
                  className={`relative mb-10 sm:mb-14 flex ${left ? 'sm:flex-row-reverse' : ''}`}
                  initial={{ opacity: 0, x: left ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                  role="article"
                  aria-labelledby={`ev-${i}`}
                >
                  <div className="w-full sm:w-1/2 px-4">
                    <div className="rounded-2xl ring-1 p-6 backdrop-blur relative overflow-hidden"
                         style={{
                           borderColor: TOKENS.line,
                           background: TOKENS.surface,
                           boxShadow: TOKENS.shadow,
                           borderStyle: ev.roadmap ? 'dashed' : 'solid'
                         }}>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{ev.icon}</span>
                        <h3 id={`ev-${i}`} className="text-lg font-semibold">
                          {ev.year}: {ev.title}
                        </h3>
                        {ev.roadmap && (
                          <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full font-semibold"
                                style={{ border: `1px solid ${TOKENS.line}`, background: TOKENS.wash }}>
                            Roadmap
                          </span>
                        )}
                      </div>
                      <p className="mt-2 text-sm" style={{ color: TOKENS.muted }}>
                        {ev.desc}
                      </p>
                    </div>
                  </div>
                  <div className="hidden sm:block w-1/2 relative">
                    <motion.div
                      className="absolute left-1/2 -translate-x-1/2 top-5 h-3 w-3 rounded-full"
                      style={{ background: 'var(--brand, #1FA550)' }}
                      animate={{ scale: [1, 1.25, 1] }}
                      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                    />
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* =================== TEAM =================== */}
      <section className="py-16 sm:py-20 lg:py-24">
        <TeamSection />
      </section>

      {/* =================== Floating CTA =================== */}
      <motion.a
        href="/contact"
        className="fixed bottom-6 right-6 rounded-xl px-6 py-3 text-sm font-semibold text-black"
        initial={{ opacity: 0, x: 80 }}
        animate={{ opacity: 1, x: 0 }}
        whileHover={{ scale: 1.04 }}
        style={{ background: 'linear-gradient(135deg, var(--brand, #1FA550), var(--accent, #06B6D4))', boxShadow: TOKENS.shadow, zIndex: 60 }}
        aria-label="Get started with MonkeyTech"
      >
        Ready to Innovate? Talk to us →
      </motion.a>

      <style>{`.font-inter{font-family:Inter,ui-sans-serif,system-ui,-apple-system,'Segoe UI',Roboto,Helvetica,Arial}`}</style>
    </main>
  );
}
